import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { LocaleProvider } from './i18n/LocaleContext'
import { BootstrapProvider, type BootstrapData } from './context/BootstrapContext'
import { Loader2 } from 'lucide-react'
import { getPreferredLocale } from './utils/locale'
import Layout from './components/layout/Layout'
import Homepage from './pages/Homepage'
import About from './pages/About'
import Activities from './pages/Activities'
import CampsIndex from './pages/CampsIndex'
import CampDetail from './pages/CampDetail'
import Faq from './pages/Faq'
import Contact from './pages/Contact'
import Terms from './pages/Terms'
import Privacy from './pages/Privacy'
import CookiePolicy from './pages/CookiePolicy'
import NotFound from './pages/NotFound'
import CookieConsent from './components/CookieConsent'
import ErrorBoundary from './components/ErrorBoundary'

const Registration = lazy(() => import('./pages/Registration'))
const PaymentResult = lazy(() => import('./pages/PaymentResult'))

// Shown while a lazy-loaded route chunk downloads, instead of a blank screen.
function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface">
      <Loader2 className="animate-spin text-primary" size={40} aria-label="Loading" />
    </div>
  )
}

function LocaleRoutes() {
  return (
    <Layout>
      <Routes>
        <Route index element={<Homepage />} />
        <Route path="rolunk" element={<About />} />
        <Route path="despre" element={<About />} />
        <Route path="tevekenysegek" element={<Activities />} />
        <Route path="activitati" element={<Activities />} />
        <Route path="taborok" element={<CampsIndex />} />
        <Route path="tabere" element={<CampsIndex />} />
        <Route path="taborok/:slug" element={<CampDetail />} />
        <Route path="tabere/:slug" element={<CampDetail />} />
        <Route path="gyik" element={<Faq />} />
        <Route path="intrebari-frecvente" element={<Faq />} />
        <Route path="kapcsolat" element={<Contact />} />
        <Route path="contact" element={<Contact />} />
        <Route path="feltetelek" element={<Terms />} />
        <Route path="termeni" element={<Terms />} />
        <Route path="adatvedelem" element={<Privacy />} />
        <Route path="confidentialitate" element={<Privacy />} />
        <Route path="cookie-szabalyzat" element={<CookiePolicy />} />
        <Route path="politica-cookies" element={<CookiePolicy />} />
        <Route path="beiratkozas" element={<Suspense fallback={<RouteFallback />}><Registration /></Suspense>} />
        <Route path="inscriere" element={<Suspense fallback={<RouteFallback />}><Registration /></Suspense>} />
        <Route path="fizetes-eredmeny" element={<Suspense fallback={<RouteFallback />}><PaymentResult /></Suspense>} />
        <Route path="rezultat-plata" element={<Suspense fallback={<RouteFallback />}><PaymentResult /></Suspense>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  )
}

function RootRedirect() {
  const locale = getPreferredLocale()
  return <Navigate to={`/${locale}`} replace />
}

export default function App({ initialData }: { initialData?: BootstrapData } = {}) {
  return (
    <ErrorBoundary>
      <BootstrapProvider initialData={initialData}>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route
            path="/hu/*"
            element={
              <LocaleProvider locale="hu">
                <LocaleRoutes />
              </LocaleProvider>
            }
          />
          <Route
            path="/ro/*"
            element={
              <LocaleProvider locale="ro">
                <LocaleRoutes />
              </LocaleProvider>
            }
          />
          <Route path="*" element={<RootRedirect />} />
        </Routes>
        <CookieConsent />
      </BootstrapProvider>
    </ErrorBoundary>
  )
}
